import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../../components/ui/card';
import { DataTable, DataTableColumn } from '../../../components/ui/data-table';
import { Button } from '../../../components/ui/button';
import ActionDropdown from '../../../components/common/action-dropdown';
import StrategyCard from '../../../components/trading/StrategyCard';
import { Plus, Filter, Users } from 'lucide-react';
import PageHeader from '../../../components/common/PageHeader';
import TradingStatusBadge from '../../../components/trading/TradingStatusBadge';
import RiskScoreBadge from '../../../components/trading/RiskScoreBadge';

type Strategy = {
  id: string;
  name: string;
  trader: string;
  traderId: string;
  roi: number;
  totalProfit: number;
  totalCopied: number;
  followers: number;
  riskLevel: 'low' | 'medium' | 'high';
  riskScore: number;
  status: 'active' | 'inactive' | 'pending';
  createdAt: string;
  minCopyAmount: number;
  maxCopyAmount: number;
  commissionRate: number;
};

const CopyTradingStrategies = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');

  const strategies: Strategy[] = [
    {
      id: 'CSTR001',
      name: 'Gold Scalper Pro',
      trader: 'John Smith',
      traderId: 'TRD001',
      roi: 42.5,
      totalProfit: 53125,
      totalCopied: 125000,
      followers: 45,
      riskLevel: 'high',
      riskScore: 8,
      status: 'active',
      createdAt: '2024-01-15',
      minCopyAmount: 100,
      maxCopyAmount: 10000,
      commissionRate: 15,
    },
    {
      id: 'CSTR002',
      name: 'Forex Trend Master',
      trader: 'Jane Doe',
      traderId: 'TRD002',
      roi: 35.8,
      totalProfit: 35084,
      totalCopied: 98000,
      followers: 38,
      riskLevel: 'medium',
      riskScore: 5,
      status: 'active',
      createdAt: '2024-01-10',
      minCopyAmount: 50,
      maxCopyAmount: 50000,
      commissionRate: 12,
    },
    {
      id: 'CSTR003',
      name: 'Conservative Growth',
      trader: 'Bob Johnson',
      traderId: 'TRD003',
      roi: 18.2,
      totalProfit: 13650,
      totalCopied: 75000,
      followers: 22,
      riskLevel: 'low',
      riskScore: 3,
      status: 'pending',
      createdAt: '2024-01-08',
      minCopyAmount: 25,
      maxCopyAmount: 25000,
      commissionRate: 8,
    },
  ];

  const columns: DataTableColumn<Strategy>[] = [
    {
      id: 'id',
      header: 'Strategy ID',
      accessor: row => <span className="font-medium">{row.id}</span>,
      sortable: true,
      sortValue: row => row.id,
      width: '120px',
    },
    {
      id: 'name',
      header: 'Strategy Name',
      accessor: row => row.name,
      sortable: true,
      sortValue: row => row.name,
      width: '180px',
    },
    {
      id: 'trader',
      header: 'Trader',
      accessor: row => row.trader,
      sortable: true,
      sortValue: row => row.trader,
      width: '150px',
    },
    {
      id: 'roi',
      header: 'ROI',
      accessor: row => (
        <span className="text-green-600 font-semibold">
          {row.roi.toFixed(2)}%
        </span>
      ),
      sortable: true,
      sortValue: row => row.roi,
      width: '100px',
    },
    {
      id: 'totalCopied',
      header: 'Total Copied',
      accessor: row => `$${row.totalCopied.toLocaleString()}`,
      sortable: true,
      sortValue: row => row.totalCopied,
      width: '140px',
    },
    {
      id: 'followers',
      header: 'Followers',
      accessor: row => (
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4" />
          <span>{row.followers}</span>
        </div>
      ),
      sortable: true,
      sortValue: row => row.followers,
      width: '120px',
    },
    {
      id: 'riskLevel',
      header: 'Risk',
      accessor: row => <RiskScoreBadge score={row.riskScore} />,
      sortable: true,
      sortValue: row => row.riskScore,
      width: '140px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => <TradingStatusBadge status={row.status} />,
      sortable: true,
      sortValue: row => row.status,
      width: '120px',
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: row => (
        <ActionDropdown
          id={row.id}
          onView={() => console.log('View strategy:', row.id)}
          onEdit={() => console.log('Edit strategy:', row.id)}
          onDelete={() => console.log('Delete strategy:', row.id)}
        />
      ),
      width: '120px',
    },
  ];

  const filteredStrategies = strategies.filter(strategy => {
    const matchesSearch =
      searchTerm === '' ||
      strategy.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      strategy.trader.toLowerCase().includes(searchTerm.toLowerCase()) ||
      strategy.id.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Strategy Management"
          description="Manage copy trading strategies and master traders"
        />
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'table' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('table')}
          >
            Table View
          </Button>
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('grid')}
          >
            Grid View
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
          <Button size="sm">
            <Plus className="h-4 w-4 mr-2" />
            New Strategy
          </Button>
        </div>
      </div>

      {viewMode === 'table' ? (
        <Card>
          <CardHeader>
            <CardTitle>All Strategies</CardTitle>
            <CardDescription>
              Manage and monitor all copy trading strategies
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={columns}
              data={filteredStrategies}
              searchPlaceholder="Search by strategy name, trader, or ID..."
              onSearchChange={setSearchTerm}
              showColumnVisibility={true}
              showExportButton={true}
              showAddButton={false}
            />
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredStrategies.map(strategy => (
            <StrategyCard
              key={strategy.id}
              id={strategy.id}
              name={strategy.name}
              manager={strategy.trader}
              roi={strategy.roi}
              totalProfit={strategy.totalProfit}
              totalInvested={strategy.totalCopied}
              investors={strategy.followers}
              riskLevel={strategy.riskLevel}
              status={strategy.status}
              onView={() => console.log('View:', strategy.id)}
              onInvest={() => console.log('Copy:', strategy.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CopyTradingStrategies;
